import { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import AdminLayout from '../components/AdminLayout';

const monthlyVisitors = [
  { month: 'Nov', visitors: 3200, pageviews: 8900, leads: 42 },
  { month: 'Dec', visitors: 2800, pageviews: 7400, leads: 31 },
  { month: 'Jan', visitors: 4100, pageviews: 11200, leads: 58 },
  { month: 'Feb', visitors: 4800, pageviews: 13500, leads: 67 },
  { month: 'Mar', visitors: 5600, pageviews: 15800, leads: 84 },
  { month: 'Apr', visitors: 6200, pageviews: 17400, leads: 96 },
];

const topPages = [
  { page: '/ (Home)', views: 6840, pct: 39 },
  { page: '/products', views: 4210, pct: 24 },
  { page: '/products/lithium-100ah', views: 2180, pct: 12 },
  { page: '/about', views: 1540, pct: 9 },
  { page: '/contact', views: 1320, pct: 8 },
  { page: '/dealer', views: 1310, pct: 8 },
];

const topCountries = [
  { country: 'United States', flag: '🇺🇸', visitors: 2480, pct: 40 },
  { country: 'Australia', flag: '🇦🇺', visitors: 930, pct: 15 },
  { country: 'United Kingdom', flag: '🇬🇧', visitors: 744, pct: 12 },
  { country: 'Germany', flag: '🇩🇪', visitors: 558, pct: 9 },
  { country: 'Sweden', flag: '🇸🇪', visitors: 372, pct: 6 },
  { country: 'UAE', flag: '🇦🇪', visitors: 310, pct: 5 },
  { country: 'Other', flag: '🌍', visitors: 806, pct: 13 },
];

const devices = [
  { label: 'Desktop', pct: 54, icon: 'ri-computer-line', accent: true },
  { label: 'Mobile', pct: 38, icon: 'ri-smartphone-line', color: 'bg-green-500' },
  { label: 'Tablet', pct: 8, icon: 'ri-tablet-line', color: 'bg-yellow-500' },
];

const sources = [
  { label: 'Organic Search', pct: 42, accent: true },
  { label: 'Direct', pct: 28, color: 'bg-green-500' },
  { label: 'Referral', pct: 18, color: 'bg-yellow-500' },
  { label: 'Social', pct: 12, color: 'bg-red-400' },
];

type Period = '7d' | '30d' | '90d';

function AnalyticsContent() {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const [period, setPeriod] = useState<Period>('30d');

  const card = dark ? 'bg-[#1C2B3A] border border-white/5' : 'bg-white border border-gray-200';
  const text = dark ? 'text-white' : 'text-gray-900';
  const muted = dark ? 'text-white/50' : 'text-gray-500';
  const divider = dark ? 'divide-white/5' : 'divide-gray-100';
  const trackBg = dark ? 'bg-white/10' : 'bg-gray-100';

  const maxVisitors = Math.max(...monthlyVisitors.map((m) => m.visitors));

  const summaryStats = [
    { icon: 'ri-user-line', label: 'Total Visitors', value: '6,200', change: '+10.7%', up: true },
    { icon: 'ri-eye-line', label: 'Page Views', value: '17,400', change: '+10.1%', up: true },
    { icon: 'ri-time-line', label: 'Avg. Session', value: '2m 48s', change: '+0:12', up: true },
    { icon: 'ri-mail-send-line', label: 'Contact Leads', value: '96', change: '+14.3%', up: true },
  ];

  return (
    <div className="space-y-6">
      {/* Period selector */}
      <div className="flex items-center justify-between">
        <p className={`text-sm ${muted}`}>Showing data for the selected period</p>
        <div className={`flex gap-1 p-1 rounded-lg ${dark ? 'bg-white/5' : 'bg-gray-100'}`}>
          {(['7d', '30d', '90d'] as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                period === p
                  ? 'text-white'
                  : dark ? 'text-white/50 hover:text-white' : 'text-gray-500 hover:text-gray-900'
              }`}
              style={period === p ? { backgroundColor: 'var(--accent)' } : {}}
            >
              {p === '7d' ? 'Last 7 days' : p === '30d' ? 'Last 30 days' : 'Last 90 days'}
            </button>
          ))}
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryStats.map((s, i) => (
          <div key={i} className={`${card} rounded-lg p-5`}>
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 flex items-center justify-center rounded-lg shrink-0" style={{ backgroundColor: 'color-mix(in srgb, var(--accent) 20%, transparent)' }}>
                <i className={`${s.icon} text-lg`} style={{ color: 'var(--accent)' }}></i>
              </div>
              <span className={`text-xs font-semibold ${s.up ? 'text-green-400' : 'text-red-400'}`}>
                {s.change}
              </span>
            </div>
            <p className={`text-2xl font-black ${text}`}>{s.value}</p>
            <p className={`text-xs mt-1 ${muted}`}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Visitor Chart */}
      <div className={`${card} rounded-lg p-6`}>
        <div className="flex items-center justify-between mb-6">
          <h2 className={`font-bold text-sm ${text}`}>Monthly Visitors &amp; Page Views</h2>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'var(--accent)' }}></div>
              <span className={`text-xs ${muted}`}>Visitors</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className={`text-xs ${muted}`}>Leads</span>
            </div>
          </div>
        </div>
        <div className="flex items-end gap-3 h-48">
          {monthlyVisitors.map((m, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full flex items-end gap-1 h-40">
                <div
                  className="flex-1 rounded-t-sm transition-all"
                  style={{ height: `${(m.visitors / maxVisitors) * 100}%`, backgroundColor: 'var(--accent)' }}
                  title={`${m.visitors.toLocaleString()} visitors`}
                ></div>
                <div
                  className="flex-1 bg-green-500 rounded-t-sm transition-all"
                  style={{ height: `${(m.leads / 100) * 100}%` }}
                  title={`${m.leads} leads`}
                ></div>
              </div>
              <span className={`text-xs ${muted}`}>{m.month}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Pages */}
        <div className={`${card} rounded-lg lg:col-span-2`}>
          <div className={`flex items-center justify-between px-5 py-4 border-b ${dark ? 'border-white/10' : 'border-gray-100'}`}>
            <h2 className={`font-bold text-sm ${text}`}>Top Pages</h2>
            <span className={`text-xs ${muted}`}>By page views</span>
          </div>
          <div className={`divide-y ${divider}`}>
            {topPages.map((p, i) => (
              <div key={i} className="px-5 py-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className={`text-sm font-medium ${text}`}>{p.page}</span>
                  <span className={`text-sm font-bold ${text}`}>{p.views.toLocaleString()}</span>
                </div>
                <div className={`w-full h-1.5 rounded-full ${trackBg}`}>
                  <div
                    className="h-1.5 rounded-full"
                    style={{ width: `${p.pct}%`, backgroundColor: 'var(--accent)' }}
                  ></div>
                </div>
                <span className={`text-xs ${muted} mt-0.5 block`}>{p.pct}% of total</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          {/* Devices */}
          <div className={`${card} rounded-lg p-5`}>
            <h2 className={`font-bold text-sm mb-4 ${text}`}>Devices</h2>
            <div className="space-y-3">
              {devices.map((d, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${d.icon} text-sm ${muted}`}></i>
                      </div>
                      <span className={`text-xs font-medium ${text}`}>{d.label}</span>
                    </div>
                    <span className={`text-xs font-bold ${text}`}>{d.pct}%</span>
                  </div>
                  <div className={`w-full h-1.5 rounded-full ${trackBg}`}>
                    {d.accent ? (
                      <div className="h-1.5 rounded-full" style={{ width: `${d.pct}%`, backgroundColor: 'var(--accent)' }}></div>
                    ) : (
                      <div className={`h-1.5 rounded-full ${d.color}`} style={{ width: `${d.pct}%` }}></div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Traffic Sources */}
          <div className={`${card} rounded-lg p-5`}>
            <h2 className={`font-bold text-sm mb-4 ${text}`}>Traffic Sources</h2>
            <div className="space-y-3">
              {sources.map((s, i) => (
                <div key={i}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-medium ${text}`}>{s.label}</span>
                    <span className={`text-xs font-bold ${text}`}>{s.pct}%</span>
                  </div>
                  <div className={`w-full h-1.5 rounded-full ${trackBg}`}>
                    {s.accent ? (
                      <div className="h-1.5 rounded-full" style={{ width: `${s.pct}%`, backgroundColor: 'var(--accent)' }}></div>
                    ) : (
                      <div className={`h-1.5 rounded-full ${s.color}`} style={{ width: `${s.pct}%` }}></div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Countries */}
      <div className={`${card} rounded-lg`}>
        <div className={`flex items-center justify-between px-5 py-4 border-b ${dark ? 'border-white/10' : 'border-gray-100'}`}>
          <h2 className={`font-bold text-sm ${text}`}>Visitors by Country</h2>
          <span className={`text-xs ${muted}`}>Top 7 countries</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 divide-y sm:divide-y-0 sm:divide-x divide-white/5">
          {topCountries.slice(0, 4).map((c, i) => (
            <div key={i} className="px-5 py-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">{c.flag}</span>
                <span className={`text-sm font-semibold ${text}`}>{c.country}</span>
              </div>
              <p className={`text-xl font-black ${text}`}>{c.visitors.toLocaleString()}</p>
              <div className={`w-full h-1 rounded-full mt-2 ${trackBg}`}>
                <div className="h-1 rounded-full" style={{ width: `${c.pct}%`, backgroundColor: 'var(--accent)' }}></div>
              </div>
              <p className={`text-xs ${muted} mt-1`}>{c.pct}% of visitors</p>
            </div>
          ))}
        </div>
        <div className={`grid grid-cols-1 sm:grid-cols-3 gap-0 divide-y sm:divide-y-0 sm:divide-x divide-white/5 border-t ${dark ? 'border-white/10' : 'border-gray-100'}`}>
          {topCountries.slice(4).map((c, i) => (
            <div key={i} className="px-5 py-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">{c.flag}</span>
                <span className={`text-sm font-semibold ${text}`}>{c.country}</span>
              </div>
              <p className={`text-xl font-black ${text}`}>{c.visitors.toLocaleString()}</p>
              <div className={`w-full h-1 rounded-full mt-2 ${trackBg}`}>
                <div className="h-1 rounded-full" style={{ width: `${c.pct}%`, backgroundColor: 'var(--accent)' }}></div>
              </div>
              <p className={`text-xs ${muted} mt-1`}>{c.pct}% of visitors</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AdminAnalyticsPage() {
  return (
    <AdminLayout title="Analytics">
      <AnalyticsContent />
    </AdminLayout>
  );
}
